/**
 * Wishlist Manager - Sheetal Zaveri Theme
 * Handles local storage, UI synchronization, header counter, and Wishlist Drawer.
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'sheetal_wishlist_items';

  class WishlistManager {
    constructor() {
      this.items = this.loadItems();
      this.initListeners();
    }

    loadItems() {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
      } catch (e) {
        console.error('Error loading wishlist from localStorage', e);
        return [];
      }
    }

    saveItems() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      } catch (e) {
        console.error('Error saving wishlist to localStorage', e);
      }
      this.notifyUpdate();
    }

    getItems() {
      return this.items;
    }

    hasItem(id) {
      if (!id) return false;
      const strId = String(id);
      return this.items.some((item) => String(item.id) === strId);
    }

    addItem(product) {
      if (!product || !product.id) return;
      const strId = String(product.id);
      if (!this.hasItem(strId)) {
        this.items.push({
          id: strId,
          title: product.title || '',
          handle: product.handle || '',
          url: product.url || '#',
          price: product.price || '',
          image: product.image || '',
          variantId: product.variantId || ''
        });
        this.saveItems();
      }
    }

    removeItem(id) {
      if (!id) return;
      const strId = String(id);
      this.items = this.items.filter((item) => String(item.id) !== strId);
      this.saveItems();
    }

    toggleItem(product) {
      if (!product || !product.id) return;
      const strId = String(product.id);
      if (this.hasItem(strId)) {
        this.removeItem(strId);
        return false;
      } else {
        this.addItem(product);
        return true;
      }
    }

    clear() {
      this.items = [];
      this.saveItems();
    }

    getCount() {
      return this.items.length;
    }

    notifyUpdate() {
      const event = new CustomEvent('wishlist:updated', {
        detail: { items: this.items, count: this.getCount() }
      });
      document.dispatchEvent(event);
      this.updateUI();
    }

    updateUI() {
      const count = this.getCount();

      // Update count bubbles across the site
      document.querySelectorAll('.wishlist-count').forEach((el) => {
        el.textContent = count;
      });

      document.querySelectorAll('.wishlist-count-bubble').forEach((el) => {
        if (count > 0) {
          el.classList.remove('hidden');
          el.style.display = 'flex';
        } else {
          el.classList.add('hidden');
          el.style.display = 'none';
        }
      });

      // Update all heart buttons
      document.querySelectorAll('[data-wishlist-id]').forEach((btn) => {
        const id = btn.getAttribute('data-wishlist-id');
        const active = this.hasItem(id);
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        
        const labelText = btn.querySelector('.wishlist-btn__text');
        if (labelText) {
          labelText.textContent = active ? 'Remove from Wishlist' : 'Add to Wishlist';
        }
      });

      // Render Drawer items if drawer exists
      this.renderDrawerItems();
    }

    renderDrawerItems() {
      const drawerList = document.getElementById('WishlistDrawerList');
      const drawerEmpty = document.getElementById('WishlistDrawerEmpty');
      const drawerFooter = document.getElementById('WishlistDrawerFooter');

      if (!drawerList) return;

      if (this.items.length === 0) {
        drawerList.innerHTML = '';
        if (drawerEmpty) drawerEmpty.style.display = 'block';
        if (drawerFooter) drawerFooter.style.display = 'none';
        return;
      }

      if (drawerEmpty) drawerEmpty.style.display = 'none';
      if (drawerFooter) drawerFooter.style.display = 'block';

      let html = '';
      this.items.forEach((item) => {
        html += `
          <div class="wishlist-drawer__item" data-wishlist-item-id="${item.id}">
            <a href="${item.url}" class="wishlist-drawer__item-image">
              ${item.image ? `<img src="${item.image}" alt="${this.escapeHtml(item.title)}" loading="lazy">` : `<div class="wishlist-drawer__placeholder"></div>`}
            </a>
            <div class="wishlist-drawer__item-info">
              <a href="${item.url}" class="wishlist-drawer__item-title">${this.escapeHtml(item.title)}</a>
              <div class="wishlist-drawer__item-price">${item.price}</div>
              <div class="wishlist-drawer__item-actions">
                <a href="${item.url}" class="wishlist-drawer__item-view">View Product</a>
                <button type="button" class="wishlist-drawer__item-remove" data-remove-id="${item.id}" aria-label="Remove item">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  <span>Remove</span>
                </button>
              </div>
            </div>
          </div>
        `;
      });

      drawerList.innerHTML = html;
    }

    escapeHtml(str) {
      return (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    initListeners() {
      // Global click handler for wishlist toggle buttons
      document.addEventListener('click', (e) => {
        const toggleBtn = e.target.closest('[data-wishlist-toggle]');
        if (toggleBtn) {
          e.preventDefault();
          e.stopPropagation();

          const productData = {
            id: toggleBtn.getAttribute('data-wishlist-id'),
            title: toggleBtn.getAttribute('data-wishlist-title'),
            handle: toggleBtn.getAttribute('data-wishlist-handle'),
            url: toggleBtn.getAttribute('data-wishlist-url'),
            price: toggleBtn.getAttribute('data-wishlist-price'),
            image: toggleBtn.getAttribute('data-wishlist-image'),
            variantId: toggleBtn.getAttribute('data-wishlist-variant-id')
          };

          const added = this.toggleItem(productData);

          // Add a subtle bounce animation
          toggleBtn.classList.add('wishlist-animate');
          setTimeout(() => toggleBtn.classList.remove('wishlist-animate'), 400);

          if (added && toggleBtn.getAttribute('data-open-drawer-on-add') === 'true') {
            this.openDrawer();
          }
          return;
        }

        // Click handler for drawer remove buttons
        const removeBtn = e.target.closest('[data-remove-id]');
        if (removeBtn) {
          e.preventDefault();
          const id = removeBtn.getAttribute('data-remove-id');
          this.removeItem(id);
          return;
        }

        // Open drawer buttons
        const openBtn = e.target.closest('[data-wishlist-open]');
        if (openBtn) {
          e.preventDefault();
          this.openDrawer();
          return;
        }

        // Close drawer buttons
        const closeBtn = e.target.closest('[data-wishlist-close]');
        if (closeBtn) {
          e.preventDefault();
          this.closeDrawer();
          return;
        }

        // Clear all button
        const clearBtn = e.target.closest('[data-wishlist-clear]');
        if (clearBtn) {
          e.preventDefault();
          this.clear();
          return;
        }
      });

      // Update UI on load
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.updateUI());
      } else {
        this.updateUI();
      }
    }

    openDrawer() {
      const drawer = document.getElementById('WishlistDrawer');
      if (drawer) {
        drawer.classList.add('is-open');
        document.body.classList.add('wishlist-drawer-open');
      }
    }

    closeDrawer() {
      const drawer = document.getElementById('WishlistDrawer');
      if (drawer) {
        drawer.classList.remove('is-open');
        document.body.classList.remove('wishlist-drawer-open');
      }
    }
  }

  window.Wishlist = new WishlistManager();
})();
